import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LiveEvent from "@mui/icons-material/LiveTv";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface StageBookingCardProps {
  booking: any;
  expanded: boolean;
  onToggle: () => void;
  tabType: "ACCEPTED" | "PENDING" | "ONGOING" | "COMPLETED" | "CANCELLED";
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
  onStartCall: (id: number, channel: string) => void;
}

const StageBookingCard: React.FC<StageBookingCardProps> = ({
  booking,
  expanded,
  onToggle,
  tabType,
  onAccept,
  onReject,
  onStartCall,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // — Netflix-like tokens (visual only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.72)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
    blue: "#3b82f6",
    gray: "#9ca3af",
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "scheduled":
        return COLORS.blue;
      case "pending":
        return "#f59e0b";
      case "ongoing":
        return "#22c55e";
      case "completed":
        return COLORS.gray;
      case "cancelled":
        return COLORS.red;
      default:
        return COLORS.gray;
    }
  };

  return (
    <Box mb={2}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 18px 48px rgba(0,0,0,0.5)",
          border: `1px solid ${COLORS.border}`,
          transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
          overflow: "hidden",
          backgroundColor: COLORS.panel,
          "&:hover": {
            transform: "translateY(-2px)",
            borderColor: "rgba(229,9,20,0.45)",
            boxShadow: "0 28px 64px rgba(229,9,20,0.12)",
          },
        }}
      >
        {/* Header */}
        <Box
          onClick={onToggle}
          sx={{
            cursor: "pointer",
            background:
              "linear-gradient(180deg, rgba(11,11,11,0.95), rgba(20,20,20,0.9))",
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderLeft: `4px solid ${COLORS.red}`,
            userSelect: "none",
          }}
          aria-expanded={expanded}
          aria-controls={`stage-booking-content-${booking.id}`}
          id={`stage-booking-header-${booking.id}`}
        >
          {/* Left: Venue name + Date/time with icon */}
          <Box display="flex" flexDirection="column" sx={{ userSelect: "text" }}>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: "16px",
                color: COLORS.text,
                letterSpacing: "-0.01em",
                mb: 0.3,
              }}
            >
              {booking.venue_name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <AccessTimeIcon sx={{ fontSize: 18, color: COLORS.gray }} />
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: COLORS.muted }}
              >
                {dayjs(booking.start_date_time).format("MMMM D, YYYY h:mm A")}
              </Typography>
            </Box>
          </Box>

          {/* Right: Status color hint + Expand icon */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: getStatusColor(booking.status),
                boxShadow: `0 0 10px ${getStatusColor(booking.status)}80`,
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              sx={{ color: COLORS.muted }}
              aria-label={expanded ? "Collapse details" : "Expand details"}
              aria-expanded={expanded}
              aria-controls={`stage-booking-content-${booking.id}`}
            >
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Stack>
        </Box>

        {/* Collapse Content */}
        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          id={`stage-booking-content-${booking.id}`}
          aria-labelledby={`stage-booking-header-${booking.id}`}
        >
          <CardContent
            sx={{
              px: { xs: 2, sm: 3 },
              pt: 2,
              pb: 3,
              backgroundColor: COLORS.panel,
              borderTop: `1px solid ${COLORS.border}`,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: COLORS.gray, fontWeight: 700 }}
                  gutterBottom
                >
                  Venue Details
                </Typography>
                <Typography variant="body2" mb={1} sx={{ color: COLORS.text }}>
                  <strong>Location:</strong> {booking.venue_location}
                </Typography>
                <Typography variant="body2" mb={1} sx={{ color: COLORS.text }}>
                  <strong>Size:</strong> {booking.venue_size}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: COLORS.gray, fontWeight: 700 }}
                  gutterBottom
                >
                  Contact Information
                </Typography>
                <Typography variant="body2" mb={1} sx={{ color: COLORS.text }}>
                  <strong>Email:</strong> {booking.contact_email}
                </Typography>
                {booking.message && (
                  <Typography variant="body2" mb={1} sx={{ color: COLORS.text }}>
                    <strong>Message:</strong> {booking.message}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Box mt={4}>
              <Stack
                direction={isMobile ? "column" : "row"}
                spacing={2}
                justifyContent="flex-end"
              >
                {tabType === "PENDING" && (
                  <>
                    <Button
                      variant="contained"
                      onClick={() => onAccept(booking.id)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 900,
                        borderRadius: 2,
                        color: "#fff",
                        px: 2.5,
                        backgroundColor: COLORS.red,
                        boxShadow: "0 12px 28px rgba(229,9,20,0.35)",
                        "&:hover": { backgroundColor: COLORS.redHover },
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onReject(booking.id)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 900,
                        borderRadius: 2,
                        px: 2.5,
                        color: COLORS.muted,
                        borderColor: COLORS.border,
                        "&:hover": {
                          borderColor: COLORS.red,
                          color: COLORS.red,
                          backgroundColor: "rgba(229,9,20,0.06)",
                        },
                      }}
                    >
                      Reject
                    </Button>
                  </>
                )}

                {(tabType === "ACCEPTED" || tabType === "ONGOING") && (
                  <Button
                    variant="contained"
                    onClick={() =>
                      onStartCall(booking.id, booking.channel_name)
                    }
                    startIcon={<LiveEvent />}
                    sx={{
                      color: "#fff",
                      fontWeight: 900,
                      textTransform: "none",
                      borderRadius: 2,
                      px: 2.5,
                      backgroundColor: COLORS.red,
                      boxShadow: "0 12px 28px rgba(229,9,20,0.35)",
                      "&:hover": { backgroundColor: COLORS.redHover },
                    }}
                  >
                    {tabType === "ACCEPTED" ? "Start Stream" : "Join Stream"}
                  </Button>
                )}
              </Stack>
            </Box>
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
};

export default StageBookingCard;
